import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { ROUTES } from "@/lib/routes";
import { formatArticleDate, getArticleBySlug } from "@/lib/articles";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.05 },
  }),
};

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const page = t.articles_page || {};
  const article = getArticleBySlug(t, slug);

  if (!article) {
    return <Navigate to={ROUTES.articles} replace />;
  }

  return (
    <main className="bg-white" data-testid={`article-page-${article.slug}`}>
      <article>
        <header className="relative overflow-hidden bg-[#faf8f8] pt-36 pb-12 sm:pt-44 sm:pb-16 lg:pt-52 lg:pb-20">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 top-20 h-72 w-72 rounded-full bg-[#fbd9e5]/55 blur-3xl"
          />
          <div className="relative mx-auto max-w-3xl px-5 sm:px-10">
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
              <Link
                to={ROUTES.articles}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#3e2723]/65 transition hover:text-[#3e2723]"
              >
                <ArrowLeft className="h-4 w-4" />
                {page.back || (lang === "en" ? "All articles" : "Toate articolele")}
              </Link>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate="visible"
              className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-bold uppercase tracking-[0.22em] text-[#3e2723]/45"
            >
              {article.publishedAt ? <time dateTime={article.publishedAt}>{formatArticleDate(article.publishedAt, lang)}</time> : null}
              {article.author ? <span>{article.author}</span> : null}
            </motion.div>

            <motion.h1
              variants={fadeUp}
              custom={2}
              initial="hidden"
              animate="visible"
              className="mt-4 font-display text-[clamp(1.75rem,5vw,3.25rem)] font-black leading-[1.08] text-[#3e2723]"
            >
              {article.title}
            </motion.h1>

            {article.excerpt ? (
              <motion.p
                variants={fadeUp}
                custom={3}
                initial="hidden"
                animate="visible"
                className="mt-5 text-base leading-relaxed text-[#7a5c55] sm:text-lg"
              >
                {article.excerpt}
              </motion.p>
            ) : null}
          </div>
        </header>

        {article.coverUrl ? (
          <div className="mx-auto max-w-5xl px-5 sm:px-10 lg:px-12">
            <motion.div
              variants={fadeUp}
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="-mt-2 overflow-hidden rounded-[1.5rem] shadow-[0_24px_60px_-28px_rgba(62,39,35,0.35)] sm:-mt-4 sm:rounded-[2rem]"
            >
              <img src={article.coverUrl} alt="" className="aspect-[16/9] w-full object-cover" loading="eager" />
            </motion.div>
          </div>
        ) : null}

        <div className="mx-auto max-w-3xl px-5 py-12 sm:px-10 sm:py-16 lg:py-20">
          <div
            className="article-prose text-[1.05rem] leading-[1.8] text-[#5c4039] sm:text-lg
              [&_a]:font-semibold [&_a]:text-[#3e2723] [&_a]:underline [&_a]:underline-offset-2
              [&_blockquote]:my-8 [&_blockquote]:border-l-4 [&_blockquote]:border-[#fbd9e5] [&_blockquote]:pl-5 [&_blockquote]:italic [&_blockquote]:text-[#3e2723]/80
              [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:text-[#3e2723]
              [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-[#3e2723]
              [&_img]:my-8 [&_img]:rounded-2xl [&_img]:shadow-sm
              [&_li]:my-1.5
              [&_ol]:my-5 [&_ol]:list-decimal [&_ol]:pl-6
              [&_p]:my-4
              [&_strong]:font-semibold [&_strong]:text-[#3e2723]
              [&_ul]:my-5 [&_ul]:list-disc [&_ul]:pl-6"
            dangerouslySetInnerHTML={{ __html: article.body || "" }}
          />
        </div>
      </article>
    </main>
  );
}
